<script lang="ts">
	import { CircleAvatar, LabelV2, Text } from '@Shelly/shelly-design-system';
	import { getAvatar } from 'src/components/Dashboards/dash-helpers';
	import { status_configurator } from 'src/helpers/device_data_helpers';
	import { getApplianceType } from 'src/helpers/device_settings_helpers';
	import {
		formatDeviceCategoryLabel,
		resolveDeviceTableStatusCell,
		type DeviceTableColumn,
	} from 'src/helpers/device_table_layout_helpers';
	import { shouldShowDashDeviceInfoLabels } from 'src/helpers/dash_device_info_labels';
	import { setSidePanelState } from 'src/components/Sidepanel/sidepanel-helpers';
	import { rooms } from 'src/stores/lists/rooms';
	import { getDeviceSettings, getDeviceStatus } from 'src/stores/device-runtime/status-selectors';
	import { defaultStateType, getDeviceState } from 'src/stores/device-runtime/states';
	import { sidePanelStateCombined } from 'src/stores/ui/sidepanel-public';
	import {
		getCriticalNotifications,
		getNotificationsForDevice,
	} from 'src/helpers/notifications/device_notifications_helper';
	import { hasBleDashStatusInvalidatingNotification } from 'src/helpers/dash_device_ble_status';
	import type { Device } from 'src/types/Device';
	import DeviceTableStateControl from './DeviceTableStateControl.svelte';

	export let device: Device;
	export let columns: DeviceTableColumn[] = [];

	$: statusStore = getDeviceStatus(device?.id);
	$: status = $statusStore;
	$: settingsStore = getDeviceSettings(device?.id);
	$: settings = $settingsStore;
	$: deviceStateStore = getDeviceState(device?.id);
	$: deviceState = $deviceStateStore;
	$: statuses = status_configurator(status, device, settings);
	$: notificationsToShow = getNotificationsForDevice(device?.id);
	$: criticalNotifications = getCriticalNotifications(notificationsToShow);
	$: suppressStatusLabels = hasBleDashStatusInvalidatingNotification(notificationsToShow);
	$: isOffline = !device || !status?.online;
	$: isUpdating = deviceState?.type === 3;
	$: notAuthenticated = deviceState?.type === 9;
	$: isDefaultState = deviceState?.type == defaultStateType;
	$: showStatusValues = shouldShowDashDeviceInfoLabels({
		device,
		statuses,
		status,
		settings,
		offline: isOffline,
		isUpdating,
		notAuthenticated,
		isDefaultState,
		hasCriticalNotification: criticalNotifications?.length > 0 || suppressStatusLabels,
	});
	$: avatar = getAvatar(device);
	$: roomName = $rooms[device?.room_id]?.name || '—';
	$: typeLabel = formatDeviceCategoryLabel(device?.category);
	$: applianceLabel = device?.appliance_type == null ? '—' : getApplianceType(device)?.title || '—';
	$: isSelected = device?.id === $sidePanelStateCombined?.id;

	const openDevice = () => {
		if (!device?.id) return;
		setSidePanelState({ type: 'device', id: device.id, open: true });
	};

	const onRowKeydown = (event: KeyboardEvent) => {
		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault();
			openDevice();
		}
	};

	const stopRowClick = (event: MouseEvent) => {
		event.stopPropagation();
	};

	const headerClass = (column: DeviceTableColumn) => {
		if (column.kind === 'name') return 'text-left device-table-name device-table-col-name';
		if (column.kind === 'state') return 'text-center device-table-state device-table-col-state';
		if (column.kind === 'room') return 'text-left device-table-col-room';
		if (column.kind === 'appliance') return 'text-left device-table-col-appliance';
		if (column.kind === 'type') return 'text-left device-table-col-type';
		return 'text-left device-table-col-status';
	};
</script>

<!-- svelte-ignore a11y_no_noninteractive_element_interactions -->
<tr
	class:is-selected={isSelected}
	class:is-offline={isOffline}
	tabindex="0"
	aria-label={device?.name}
	on:click={openDevice}
	on:keydown={onRowKeydown}
>
	{#each columns as column (column.id)}
		{#if column.kind === 'name'}
			<th class={headerClass(column)}>
				<div class="device-table-name-inner">
					<CircleAvatar
						size="s"
						image={avatar?.image}
						icon={avatar?.icon}
						noBackground={avatar?.noBackground}
						backgroundColor={avatar?.backgroundColor || undefined}
					/>
					<Text
						element="span"
						styleType="body-extra-small"
						noWrap
						class="device-table-name-text"
					>
						{device?.name || device?.id}
					</Text>
				</div>
			</th>
		{:else if column.kind === 'state'}
			<td class={headerClass(column)} on:click={stopRowClick}>
				<DeviceTableStateControl {device} {status} {settings} />
			</td>
		{:else if column.kind === 'room'}
			<td class={headerClass(column)}>{roomName}</td>
		{:else if column.kind === 'appliance'}
			<td class="{headerClass(column)} text-capitalize">{applianceLabel}</td>
		{:else if column.kind === 'type'}
			<td class="{headerClass(column)} text-capitalize">{typeLabel}</td>
		{:else}
			{@const cell =
				showStatusValues && column.status
					? resolveDeviceTableStatusCell(statuses, column.status)
					: null}
			<td class={headerClass(column)}>
				{#if cell}
					<LabelV2 size="xs" value={cell.value} icon={cell.icon} noBackground noPadding />
				{:else}
					<span class="text-color-muted">—</span>
				{/if}
			</td>
		{/if}
	{/each}
</tr>
